import { parameterUtils, type ModelData, type ModelParameter } from '@/shared/stores/model-utils'
import {
  frameThickness,
  glassHingeWidth,
  glassHingeHeight,
  glassLockWidth,
  glassLockHeight,
} from '@/shared/config/model-constants'
import type { DimensionHandler } from '@/shared/stores/create-model-store'

function getParam(params: ModelParameter[], id: string) {
  return parameterUtils.findParameter(params, id)
}

function updateParam(model: ModelData, id: string, updates: Partial<ModelParameter>) {
  model.parameters = parameterUtils.updateParameterInArray(model.parameters!, id, updates)
}

const doorPanelIds = ['left_door_glass_panel_1', 'right_door_glass_panel'] as const
const fixedPanelIds = ['left_glass_panel', 'center_glass_panel', 'right_glass_panel'] as const
const bottomFrameIds = ['bottom_left_frame_profile', 'bottom_center_frame_profile', 'bottom_right_frame_profile'] as const

/** Template8 Layout: [LeftFrame][LeftGlass][LeftDoorSection][CenterGlass][RightDoorSection][RightGlass][RightFrame] */
function recalculatePositions(model: ModelData) {
  const params = model.parameters!
  const leftFrame = getParam(params, 'left_frame_profile')
  const rightFrame = getParam(params, 'right_frame_profile')
  const topFrame = getParam(params, 'top_frame_profile')
  if (!leftFrame || !rightFrame || !topFrame) return

  let currentX = leftFrame.width
  const topY = topFrame.height

  const leftGlass = getParam(params, 'left_glass_panel')
  const leftDoorTop = getParam(params, 'left_door_top_glass_panel')
  const leftDoorMain = getParam(params, 'left_door_glass_panel_1')
  const centerGlass = getParam(params, 'center_glass_panel')
  const rightDoorTop = getParam(params, 'right_door_top_glass_panel')
  const rightDoorMain = getParam(params, 'right_door_glass_panel')
  const rightGlass = getParam(params, 'right_glass_panel')

  if (leftGlass) {
    updateParam(model, 'left_glass_panel', { position: { x: currentX, y: topY, z: 0 } })
    currentX += leftGlass.width
  }

  if (leftDoorTop && leftDoorMain) {
    updateParam(model, 'left_door_top_glass_panel', { position: { x: currentX, y: topY, z: 0 } })
    updateParam(model, 'left_door_glass_panel_1', { position: { x: currentX, y: topY + leftDoorTop.height, z: 0 } })
    currentX += leftDoorMain.width
  }

  if (centerGlass) {
    updateParam(model, 'center_glass_panel', { position: { x: currentX, y: topY, z: 0 } })
    currentX += centerGlass.width
  }

  if (rightDoorTop && rightDoorMain) {
    updateParam(model, 'right_door_top_glass_panel', { position: { x: currentX, y: topY, z: 0 } })
    updateParam(model, 'right_door_glass_panel', { position: { x: currentX, y: topY + rightDoorTop.height, z: 0 } })
    currentX += rightDoorMain.width
  }

  if (rightGlass) {
    updateParam(model, 'right_glass_panel', { position: { x: currentX, y: topY, z: 0 } })
  }

  // Update bottom frames
  const bottomY = (model.height as number) - topFrame.height
  if (leftGlass) {
    updateParam(model, 'bottom_left_frame_profile', { position: { x: leftFrame.width, y: bottomY, z: 0 }, width: leftGlass.width })
  }
  if (centerGlass) {
    const updatedCenter = getParam(model.parameters!, 'center_glass_panel')
    if (updatedCenter) {
      updateParam(model, 'bottom_center_frame_profile', { position: { x: updatedCenter.position!.x, y: bottomY, z: 0 }, width: centerGlass.width })
    }
  }
  if (rightGlass) {
    const updatedRight = getParam(model.parameters!, 'right_glass_panel')
    if (updatedRight) {
      updateParam(model, 'bottom_right_frame_profile', { position: { x: updatedRight.position!.x, y: bottomY, z: 0 }, width: rightGlass.width })
    }
  }

  updateParam(model, 'right_frame_profile', { position: { x: (model.width as number) - rightFrame.width, y: 0, z: 0 } })
}

function updateGlassPanelHardwarePositionsX(model: ModelData, panelWidth: number, panelId: string) {
  const panel = getParam(model.parameters!, panelId)
  if (!panel?.hardware) return

  const updatedHardware = { ...panel.hardware }
  const isRightDoor = panelId === 'right_door_glass_panel'

  if (updatedHardware.locks) {
    updatedHardware.locks = updatedHardware.locks.map((lock) => {
      if (panelId === 'left_door_glass_panel_1') {
        return { ...lock, position: { ...lock.position, x: panelWidth - glassLockWidth * 1.5 } }
      }
      if (isRightDoor && lock.positionId === 'door_2_left_lock') {
        return { ...lock, position: { ...lock.position, x: glassLockWidth } }
      }
      const ratio = panel.width > 0 ? lock.position.x / panel.width : 0.9
      return { ...lock, position: { ...lock.position, x: panelWidth * ratio } }
    })
  }
  if (updatedHardware.handles) {
    updatedHardware.handles = updatedHardware.handles.map((handle) => {
      const ratio = panel.width > 0 ? handle.position.x / panel.width : 0.5
      return { ...handle, position: { ...handle.position, x: panelWidth * ratio } }
    })
  }
  if (isRightDoor && updatedHardware.hinges) {
    updatedHardware.hinges = updatedHardware.hinges.map((hinge) => {
      if (hinge.positionId === 'door_2_right_top_hinge_1') {
        return { ...hinge, position: { ...hinge.position, x: panelWidth - glassHingeWidth / 2 } }
      }
      if (hinge.positionId === 'door_2_right_bottom_hinge_2') {
        return { ...hinge, position: { ...hinge.position, x: panelWidth - glassHingeWidth } }
      }
      return hinge
    })
  }
  updateParam(model, panelId, { hardware: updatedHardware })
}

function updateDoorHardwareForHeight(model: ModelData, doorHeight: number, panelId: string) {
  const panel = getParam(model.parameters!, panelId)
  if (!panel?.hardware) return

  const updatedHardware = { ...panel.hardware }
  if (updatedHardware.handles) {
    updatedHardware.handles = updatedHardware.handles.map((h) => ({ ...h, position: { ...h.position, y: doorHeight * 0.55 } }))
  }
  if (updatedHardware.hinges) {
    updatedHardware.hinges = updatedHardware.hinges.map((hinge) => {
      if (hinge.position.y < 0) return hinge
      return { ...hinge, position: { ...hinge.position, y: doorHeight - glassHingeHeight } }
    })
  }
  if (updatedHardware.locks) {
    updatedHardware.locks = updatedHardware.locks.map((lock) => ({ ...lock, position: { ...lock.position, y: doorHeight - glassLockHeight } }))
  }
  updateParam(model, panelId, { hardware: updatedHardware })
}

const totalWidth: DimensionHandler = (model, _initial, numValue) => {
  const diff = numValue - (model.width as number)
  model.width = numValue
  updateParam(model, 'top_frame_profile', { width: numValue })

  if (diff !== 0) {
    const fixedPanels = fixedPanelIds.map((id) => getParam(model.parameters!, id)).filter(Boolean)
    const share = diff / fixedPanels.length
    fixedPanels.forEach((panel) => {
      updateParam(model, panel!.positionId, { width: Math.max(100, panel!.width + share) })
    })
    recalculatePositions(model)
  }
  return model
}

const totalHeight: DimensionHandler = (model, _initial, numValue) => {
  model.height = numValue
  updateParam(model, 'left_frame_profile', { height: numValue })
  updateParam(model, 'right_frame_profile', { height: numValue })

  const topFrame = getParam(model.parameters!, 'top_frame_profile')
  const leftDoorTop = getParam(model.parameters!, 'left_door_top_glass_panel')
  const rightDoorTop = getParam(model.parameters!, 'right_door_top_glass_panel')

  if (topFrame) {
    const availableHeight = numValue - topFrame.height * 2
    fixedPanelIds.forEach((id) => {
      if (getParam(model.parameters!, id)) updateParam(model, id, { height: availableHeight })
    })

    const bottomY = numValue - topFrame.height
    bottomFrameIds.forEach((id) => {
      const f = getParam(model.parameters!, id)
      if (f) updateParam(model, id, { position: { ...f.position, y: bottomY } })
    })

    if (leftDoorTop && rightDoorTop) {
      const doorHeight = numValue - topFrame.height - leftDoorTop.height
      const doorY = topFrame.height + leftDoorTop.height
      doorPanelIds.forEach((id) => {
        const p = getParam(model.parameters!, id)
        if (p) {
          updateParam(model, id, { height: doorHeight, position: { ...p.position, y: doorY } })
          updateDoorHardwareForHeight(model, doorHeight, id)
        }
      })
    }
  }
  return model
}

const transomHeight: DimensionHandler = (model, _initial, numValue) => {
  const leftDoorTop = getParam(model.parameters!, 'left_door_top_glass_panel')
  const rightDoorTop = getParam(model.parameters!, 'right_door_top_glass_panel')
  const leftDoor = getParam(model.parameters!, 'left_door_glass_panel_1')
  const topFrame = getParam(model.parameters!, 'top_frame_profile')
  if (!leftDoorTop || !rightDoorTop || !leftDoor || !topFrame) return model

  const newPanelHeight = numValue - topFrame.height
  updateParam(model, 'left_door_top_glass_panel', { height: newPanelHeight, position: { ...leftDoorTop.position, y: frameThickness } })
  updateParam(model, 'right_door_top_glass_panel', { height: newPanelHeight, position: { ...rightDoorTop.position, y: frameThickness } })

  doorPanelIds.forEach((id) => {
    const p = getParam(model.parameters!, id)
    if (p) updateParam(model, id, { position: { ...p.position, y: numValue } })
  })

  const newTotalHeight = numValue + leftDoor.height
  model.height = newTotalHeight
  updateParam(model, 'left_frame_profile', { height: newTotalHeight })
  updateParam(model, 'right_frame_profile', { height: newTotalHeight })

  fixedPanelIds.forEach((id) => {
    if (getParam(model.parameters!, id)) updateParam(model, id, { height: newTotalHeight - frameThickness - frameThickness })
  })

  bottomFrameIds.forEach((id) => {
    const f = getParam(model.parameters!, id)
    if (f) updateParam(model, id, { position: { ...f.position, y: newTotalHeight - frameThickness } })
  })

  return model
}

const doorPanelHeight: DimensionHandler = (model, _initial, numValue) => {
  const leftDoorTop = getParam(model.parameters!, 'left_door_top_glass_panel')
  if (!leftDoorTop) return model

  doorPanelIds.forEach((id) => {
    if (getParam(model.parameters!, id)) {
      updateParam(model, id, { height: numValue })
      updateDoorHardwareForHeight(model, numValue, id)
    }
  })

  const topFrame = getParam(model.parameters!, 'top_frame_profile')
  if (topFrame) {
    const newTotalHeight = topFrame.height + leftDoorTop.height + numValue
    model.height = newTotalHeight
    updateParam(model, 'left_frame_profile', { height: newTotalHeight })
    updateParam(model, 'right_frame_profile', { height: newTotalHeight })

    fixedPanelIds.forEach((id) => {
      if (getParam(model.parameters!, id)) updateParam(model, id, { height: newTotalHeight - frameThickness - frameThickness })
    })

    bottomFrameIds.forEach((id) => {
      const f = getParam(model.parameters!, id)
      if (f) updateParam(model, id, { position: { ...f.position, y: newTotalHeight - frameThickness } })
    })
  }

  recalculatePositions(model)
  return model
}

const leftFrameWidth: DimensionHandler = (model, _initial, numValue) => {
  const leftGlass = getParam(model.parameters!, 'left_glass_panel')
  const leftFrame = getParam(model.parameters!, 'left_frame_profile')
  if (!leftGlass || !leftFrame) return model

  const currentSectionWidth = leftFrame.width + leftGlass.width
  const diff = numValue - currentSectionWidth
  updateParam(model, 'left_glass_panel', { width: Math.max(100, numValue - leftFrame.width) })
  model.width = (model.width as number) + diff
  updateParam(model, 'top_frame_profile', { width: model.width as number })
  recalculatePositions(model)
  return model
}

const centerFrameWidth: DimensionHandler = (model, _initial, numValue) => {
  const center = getParam(model.parameters!, 'center_glass_panel')
  if (!center) return model

  const diff = numValue - center.width
  updateParam(model, 'center_glass_panel', { width: numValue })
  model.width = (model.width as number) + diff
  updateParam(model, 'top_frame_profile', { width: model.width as number })
  recalculatePositions(model)
  return model
}

const rightFrameWidth: DimensionHandler = (model, _initial, numValue) => {
  const rightGlass = getParam(model.parameters!, 'right_glass_panel')
  const rightFrame = getParam(model.parameters!, 'right_frame_profile')
  if (!rightGlass || !rightFrame) return model

  const currentSectionWidth = rightGlass.width + rightFrame.width
  const diff = numValue - currentSectionWidth
  updateParam(model, 'right_glass_panel', { width: Math.max(100, numValue - rightFrame.width) })
  model.width = (model.width as number) + diff
  updateParam(model, 'top_frame_profile', { width: model.width as number })
  recalculatePositions(model)
  return model
}

const leftDoorWidth: DimensionHandler = (model, _initial, numValue) => {
  const leftDoor = getParam(model.parameters!, 'left_door_glass_panel_1')
  const leftDoorTop = getParam(model.parameters!, 'left_door_top_glass_panel')
  if (!leftDoor || !leftDoorTop) return model

  const diff = numValue - leftDoor.width
  updateParam(model, 'left_door_glass_panel_1', { width: numValue })
  updateParam(model, 'left_door_top_glass_panel', { width: numValue })
  updateGlassPanelHardwarePositionsX(model, numValue, 'left_door_glass_panel_1')

  model.width = (model.width as number) + diff
  updateParam(model, 'top_frame_profile', { width: model.width as number })

  const rightDoor = getParam(model.parameters!, 'right_door_glass_panel')
  model.doorConfiguration = {
    ...model.doorConfiguration!,
    totalDoorWidth: numValue + (rightDoor?.width || 0),
    individualDoorWidth: [numValue, rightDoor?.width || 0],
  }

  recalculatePositions(model)
  return model
}

const rightDoorWidth: DimensionHandler = (model, _initial, numValue) => {
  const rightDoor = getParam(model.parameters!, 'right_door_glass_panel')
  const rightDoorTop = getParam(model.parameters!, 'right_door_top_glass_panel')
  if (!rightDoor || !rightDoorTop) return model

  const diff = numValue - rightDoor.width
  updateParam(model, 'right_door_glass_panel', { width: numValue })
  updateParam(model, 'right_door_top_glass_panel', { width: numValue })
  updateGlassPanelHardwarePositionsX(model, numValue, 'right_door_glass_panel')

  model.width = (model.width as number) + diff
  updateParam(model, 'top_frame_profile', { width: model.width as number })

  const leftDoor = getParam(model.parameters!, 'left_door_glass_panel_1')
  model.doorConfiguration = {
    ...model.doorConfiguration!,
    totalDoorWidth: (leftDoor?.width || 0) + numValue,
    individualDoorWidth: [leftDoor?.width || 0, numValue],
  }

  recalculatePositions(model)
  return model
}

export const model8DimensionHandlers: Record<string, DimensionHandler> = {
  totalWidth,
  totalHeight,
  transomHeight,
  doorPanelHeight,
  leftFrameWidth,
  centerFrameWidth,
  rightFrameWidth,
  leftDoorWidth,
  rightDoorWidth,
}
